import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Switch,
} from "@mui/material";
import { Select, Dropdown } from "semantic-ui-react";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const AssignDegreesDialog = ({
  showAssignDegreesModal,
  setShowAssignDegreesModal,
}) => {
  const [degrees, setDegrees] = useState([]);
  const [selected, setSelected] = useState(new Map()); // Map to track selected degrees and their quotas

  // Fetch degrees from API
  useEffect(() => {
    const fetchDegrees = async () => {
      try {
        const response = await fetch(`${BASE_URL}/degree/get_degrees`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch degrees");
        }
        const data = await response.json();
        setDegrees(data.degrees);
      } catch (error) {
        console.error("Error fetching degrees:", error);
      }
    };

    if (showAssignDegreesModal) {
      fetchDegrees();
    }
  }, [showAssignDegreesModal]);

  // Handle toggle selection
  const handleToggle = (id) => {
    setSelected((prev) => {
      const newSelected = new Map(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.set(id, {
          quotas: [
            {
              quota: "",
              seats: "",
              preRequisite: [],
              minMarks: "",
              entryTestRequired: false,
              meritFormula: {
                metric: "",
                fsc: "",
                bsc: "",
                bs: "",
                mphil: "",
                phd: "",
                entryTest: "",
              },
            },
          ],
        });
      }
      return newSelected;
    });
  };

  // Update a specific field within a quota
  const updateQuotaField = (id, index, field, value) => {
    setSelected((prev) => {
      const newSelected = new Map(prev);
      if (newSelected.has(id)) {
        const degreeData = newSelected.get(id);
        const quota = degreeData.quotas[index];

        if (field.includes(".")) {
          const [parent, child] = field.split(".");
          quota[parent][child] = value;
        } else {
          quota[field] = value;
        }

        degreeData.quotas[index] = { ...quota };
        newSelected.set(id, { ...degreeData });
      }
      return newSelected;
    });
  };

  // Add a new quota to a degree
  const addQuota = (id) => {
    setSelected((prev) => {
      const newSelected = new Map(prev);
      if (newSelected.has(id)) {
        const degreeData = newSelected.get(id);
        degreeData.quotas.push({
          quota: "",
          seats: "",
          preRequisite: [],
          minMarks: "",
          entryTestRequired: false,
          meritFormula: {
            metric: "",
            fsc: "",
            bsc: "",
            bs: "",
            mphil: "",
            phd: "",
            entryTest: "",
          },
        });
        newSelected.set(id, { ...degreeData });
      }
      return newSelected;
    });
  };

  // Remove a quota from a degree
  const removeQuota = (id, index) => {
    setSelected((prev) => {
      const newSelected = new Map(prev);
      if (newSelected.has(id)) {
        const degreeData = newSelected.get(id);
        degreeData.quotas.splice(index, 1);
        newSelected.set(id, { ...degreeData });
      }
      return newSelected;
    });
  };

  // Save selected degrees
  const handleSave = async () => {
    const selectedDegrees = Array.from(selected.entries()).map(
      ([id, data]) => ({
        degreeId: id,
        quotas: data.quotas,
      })
    );
    console.log("selectedDegrees", selectedDegrees);
    // try {
    //   await fetch(`${BASE_URL}/assign-degrees`, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //       Authorization: `Bearer ${token}`,
    //     },
    //     body: JSON.stringify({ degrees: selectedDegrees }),
    //   });
    //   console.log("Degrees assigned successfully");
    // } catch (error) {
    //   console.error("Failed to assign degrees:", error);
    // }

    // setShowAssignDegreesModal(false);
  };

  const quotaOptions = [
    { key: "open", value: "Open", text: "Open" },
    { key: "reserved", value: "Reserved", text: "Reserved" },
  ];

  const preRequisiteOptions = [
    { key: "none", value: "None", text: "None" },
    { key: "fsc", value: "FSc", text: "FSc" },
    { key: "bsc", value: "BSc", text: "BSc" },
  ];

  return (
    <Dialog
      open={showAssignDegreesModal}
      onClose={() => setShowAssignDegreesModal(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Assign Programs To Batch</DialogTitle>
      <DialogContent>
        <List>
          {degrees.map((degree) => (
            <div key={degree.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleToggle(degree.id)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selected.has(degree.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={degree.name} />
                </ListItemButton>
              </ListItem>
              {selected.has(degree.id) && (
                <div style={{ marginLeft: "2rem", marginTop: "1rem" }}>
                  {selected.get(degree.id)?.quotas?.map((quota, index) => (
                    <div key={index} style={{ marginBottom: "2rem" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "1rem",
                        }}
                      >
                        <Select
                          placeholder="Select Quota"
                          options={quotaOptions}
                          value={quota.quota}
                          onChange={(e, { value }) =>
                            updateQuotaField(degree.id, index, "quota", value)
                          }
                          style={{ flex: 1, marginRight: "1rem" }}
                        />
                        <input
                          type="number"
                          placeholder="Seats"
                          value={quota.seats}
                          onChange={(e) =>
                            updateQuotaField(
                              degree.id,
                              index,
                              "seats",
                              e.target.value
                            )
                          }
                          className="form-control"
                          style={{ flex: 1 }}
                        />
                      </div>
                      <Switch
                          checked={quota.entryTestRequired}
                          onChange={(e) =>
                            updateQuotaField(
                              degree.id,
                              index,
                              "entryTestRequired",
                              e.target.checked
                            )
                          }
                        />
                        <span style={{ marginLeft: "0.5rem" }}>
                          Entry Test Required
                        </span>
                        <h5 style={{ marginTop: "1rem", marginTop: "1rem"}}>Eligibility Criteria against Qouta for selected Program</h5>
                      <Dropdown
                        placeholder="Select Pre-Requisites"
                        fluid
                        multiple
                        selection
                        options={preRequisiteOptions}
                        value={quota.preRequisite}
                        onChange={(e, { value }) =>
                          updateQuotaField(
                            degree.id,
                            index,
                            "preRequisite",
                            value
                          )
                        }
                        style={{ marginBottom: "1rem", width: "100%" , marginTop: "1rem"}}
                      />
                      <input
                        name="minMarks"
                        type="number"
                        value={quota.minMarks}
                        onChange={(e) =>
                          updateQuotaField(
                            degree.id,
                            index,
                            "minMarks",
                            e.target.value
                          )
                        }
                        className="form-control"
                        placeholder="% Mininum Marks"
                        style={{ marginBottom: "1rem", width: "100%" }}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "1rem",
                        }}
                      >
                      </div>
                      <div>
                        <h5>Merit Formula against Quota for Merit List</h5>
                        {Object.keys(quota.meritFormula).map((field) => {
                          let fieldName = field; // Default to the field name
                          if (field === "metric") {
                            fieldName = "Metric / O-Level or Equivalent"; // Set custom name for "metric"
                          } else if (field === "entryTest") {
                            fieldName = "Entry Test or Equivalent";
                          } else if (field === "bsc") {
                              fieldName = "BSc or Equivalent";
                          } else if (field === "bs") {
                              fieldName = "BS or Equivalent";
                          } else if (field === "phd") {
                              fieldName = "PhD or Equivalent";
                          } else if (field === "mphil") {
                              fieldName = "MPhil or Equivalent";
                          } else if (field === "fsc") {
                              fieldName = "FSC / A Level / ICOM or Equivalent";
                          }
                          return (
                            <div key={field} style={{ marginTop: "1rem" }}>
                              <input
                                type="number"
                                placeholder={`Enter percentage of ${fieldName}`} // Use fieldName here
                                value={quota.meritFormula[field]}
                                onChange={(e) =>
                                  updateQuotaField(
                                    degree.id,
                                    index,
                                    `meritFormula.${field}`,
                                    e.target.value
                                  )
                                }
                                className="form-control"
                                style={{
                                  marginBottom: "0.5rem",
                                  width: "100%",
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <Button
                        onClick={() => addQuota(degree.id)}
                        className="dialog-actions-button positive"
                        style={{ marginTop: "1rem" }}
                      >
                        Add
                      </Button>
                      <Button
                        onClick={() => removeQuota(degree.id, index)}
                        className="dialog-actions-button negative"
                        style={{ marginLeft: "1rem", marginTop: "1rem" }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          className="dialog-actions-button negative"
          onClick={() => setShowAssignDegreesModal(false)}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} className="dialog-actions-button positive">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDegreesDialog;
